import React from 'react';
import { Target, Compass, Rocket } from 'lucide-react';

const pillars = [
  {
    icon: Target,
    label: 'Our Mission',
    body:
      'To advance education, workforce development, entrepreneurship, and leadership across transportation, logistics, and supply chain — creating opportunity where it is most needed.',
  },
  {
    icon: Compass,
    label: 'Our Vision',
    body:
      'A region where every learner and worker has a clear, connected pathway from classroom to career to leadership — powered by aligned employers, educators, and community.',
  },
  {
    icon: Rocket,
    label: 'Long-Term Goals',
    body:
      'Convene an annual regional collaborative that builds durable partnerships, seeds new career pathways, and produces a measurable increase in economic mobility year after year.',
  },
];

export const About = () => {
  return (
    <section id="about" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-5">
            <span className="overline text-aawta-magenta">About the Collaborative</span>
            <h2 className="font-display font-bold text-4xl md:text-5xl mt-4 text-aawta-navy tracking-tight leading-[1.1]">
              A regional convener for the future workforce.
            </h2>
            <div className="divider-magenta mt-6" />
            <p className="mt-6 text-[17px] leading-relaxed text-aawta-muted">
              AAWTA created the Education &amp; Industry Workforce Collaborative because the
              region's workforce challenges cannot be solved by any single sector alone.
              Employers need talent. Educators need clear industry signals. Communities need
              access to careers with real economic mobility.
            </p>
            <p className="mt-4 text-[17px] leading-relaxed text-aawta-muted">
              We convene the leaders who can move the system — and translate the conversation
              into commitments, hires, internships, and lasting pathways.
            </p>
          </div>

          <div className="lg:col-span-7 space-y-5">
            {pillars.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.label}
                  data-testid={`about-pillar-${p.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className="group flex gap-5 p-6 md:p-7 rounded-2xl bg-aawta-surface border border-aawta-border hover:bg-white hover:shadow-aawta-elevated hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="shrink-0 w-12 h-12 rounded-xl bg-aawta-navy text-white flex items-center justify-center group-hover:bg-aawta-magenta transition-colors duration-300">
                    <Icon className="h-5 w-5" strokeWidth={2} />
                  </div>
                  <div>
                    <div className="overline text-aawta-navy/70">{p.label}</div>
                    <p className="mt-2 text-[16px] leading-relaxed text-aawta-navy/90">
                      {p.body}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Vision video */}
        <div className="mt-20 md:mt-24 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <span className="overline text-aawta-magenta">See the Vision</span>
            <h3 className="font-display font-bold text-3xl md:text-4xl lg:text-5xl mt-3 text-aawta-navy tracking-tight leading-[1.1]">
              See the Vision Behind the Collaborative
            </h3>
            <div className="divider-magenta mt-5" />
            <p className="mt-5 text-[16.5px] leading-relaxed text-aawta-muted max-w-2xl">
              See how AAWTA's Education &amp; Industry Workforce Collaborative is bringing
              education, industry, workforce development, and community leaders together to
              build stronger career pathways, create opportunities, and prepare the next
              generation of talent.
            </p>
          </div>
          <div className="lg:col-span-5 order-1 lg:order-2">
            <div className="relative mx-auto w-full max-w-[300px] sm:max-w-[340px] lg:max-w-none">
              <div className="absolute -inset-3 bg-aawta-magenta/20 blur-2xl rounded-3xl" aria-hidden />
              <div
                data-testid="about-vision-video"
                className="relative rounded-3xl overflow-hidden border-2 border-white shadow-aawta-elevated bg-black"
                style={{ aspectRatio: '9 / 16' }}
              >
                <iframe
                  src="https://www.youtube.com/embed/vK5EtWf8x0Y?rel=0&modestbranding=1&playsinline=1"
                  title="See the Vision Behind the AAWTA Collaborative"
                  frameBorder="0"
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
